import { useState } from 'react';
import { Box, Stack, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { Input } from '@liminal/web-ui/forms/Input';
import { Select } from '@liminal/web-ui/forms/Select';
import { SegmentedToggle } from '@liminal/web-ui/forms/SegmentedToggle';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { formatOrderDate } from '@liminal/utils/date';
import { RewardPayment_State } from '@/api/generated';
import type { RewardPayment } from '@/api/types';
import { useUrlState } from '@/hooks/useUrlState';
import { useRewardMerchants, useRewardPayments, useRewardProgramme } from '@/hooks/useRewards';
import { paymentState, redemptionState } from '@/components/ui/statusHelpers';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { durationSeconds, durationText, groupDecimal, isPositive, paymentFilter, usdText } from '@/lib/rewards';
import { PaymentDetails } from '../components/PaymentDetails';

const PAGE_SIZE = 50;
/** Partners are a handful; one page names every payment's. */
const MERCHANT_NAMES_PAGE = 200;
/** A review queue longer than this is shown as "200+". */
const REVIEW_COUNT_PAGE = 200;

const COLUMNS = ['Settled', 'Customer', 'Partner', 'Paid', 'Choice', 'Status'] as const;

const STATE_OPTIONS = [
  { value: '', label: 'Any status' },
  ...Object.values(RewardPayment_State).map((state) => ({ value: state, label: paymentState(state).label })),
];

const FILTER_DEFAULTS = { merchant: '', state: '', from: '', to: '', owedBefore: '' };

/** The quick views over the list: everything, the review queue, and Pay with LIL choices owed past the alert window. */
type View = 'all' | 'review' | 'owed';

function viewOf(filters: typeof FILTER_DEFAULTS): View {
  if (filters.owedBefore) return 'owed';
  if (filters.state === RewardPayment_State.STATE_REVIEW) return 'review';
  return 'all';
}

/**
 * What the customer chose, read off what the choice left behind: a
 * redemption for Pay with LIL, a grant for Earn. A payment still open (a
 * failed Pay with LIL reopens it) or refunded before a choice has none.
 */
function choiceText(p: RewardPayment): string {
  if (p.state === RewardPayment_State.STATE_OPEN || p.state === RewardPayment_State.STATE_REFUNDED) return 'Not chosen';
  // A refund after the choice reprices the amounts, to zero for a full one.
  if (p.redemption) return isPositive(p.redeemLil) ? `Pay with ${groupDecimal(p.redeemLil)} LIL` : 'Pay with LIL';
  if (p.grantId) return isPositive(p.earnLil) ? `Earn ${groupDecimal(p.earnLil)} LIL` : 'Earn LIL';
  return '—';
}

/** Every reward payment, newest first, with quick views for the ones that need a person. It reads only. */
export function PaymentsPage() {
  const [filters, setFilters] = useUrlState(FILTER_DEFAULTS);
  const [selected, setSelected] = useState<RewardPayment | null>(null);
  const pagination = useCursorPagination(PAGE_SIZE);
  const programme = useRewardProgramme();
  // How long a redemption may stay owed before it counts, from the programme
  // figures; null until they load, which keeps the Owed view off.
  const owedWindow = durationSeconds(programme.data?.owedAlertDuration);
  const payments = useRewardPayments(paymentFilter(filters), PAGE_SIZE, pagination.pageToken ?? '');
  const review = useRewardPayments(`state = ${RewardPayment_State.STATE_REVIEW}`, REVIEW_COUNT_PAGE, '');
  const merchants = useRewardMerchants(MERCHANT_NAMES_PAGE, '');
  const merchantNames = new Map((merchants.data?.rewardMerchants ?? []).map((m) => [m.name, m.displayName ?? m.name]));
  const rows = payments.data?.rewardPayments ?? [];
  const nextPageToken = payments.data?.nextPageToken ?? '';
  const view = viewOf(filters);

  const reviewRows = review.data?.rewardPayments?.length ?? 0;
  const reviewCount = review.isLoading ? '…' : `${reviewRows}${review.data?.nextPageToken ? '+' : ''}`;
  const owedCount = programme.isLoading ? '…' : String(programme.data?.owedTooLongCount ?? 0);
  const filtered = Object.values(filters).some((value) => value !== '');

  const setFilter = (patch: Partial<typeof FILTER_DEFAULTS>) => {
    setFilters(patch);
    pagination.reset();
  };

  const setView = (next: View) => {
    if (next === 'all') setFilter({ state: '', owedBefore: '' });
    if (next === 'review') setFilter({ state: RewardPayment_State.STATE_REVIEW, owedBefore: '' });
    if (next === 'owed' && owedWindow !== null) {
      setFilter({
        state: RewardPayment_State.STATE_PAYING,
        owedBefore: new Date(Date.now() - owedWindow * 1000).toISOString(),
      });
    }
  };

  return (
    <Stack spacing={3}>
      <Box sx={{ maxWidth: 560 }}>
        <SegmentedToggle<View>
          aria-label="Payments view"
          size="sm"
          value={view}
          onChange={setView}
          options={[
            { value: 'all', label: 'All payments' },
            { value: 'review', label: `Needs review (${reviewCount})` },
            {
              value: 'owed',
              label: `Owed too long (${owedCount})`,
              disabled: owedWindow === null,
              tooltip:
                owedWindow === null
                  ? undefined
                  : `Pay with LIL choices whose LIL was taken but whose money has not moved for ${durationText(owedWindow)}`,
            },
          ]}
        />
      </Box>

      {/* Top-aligned: the date inputs reserve a message line under the field, the selects do not. */}
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="flex-start">
        <Select
          variant="filter"
          aria-label="Partner"
          size="sm"
          width={220}
          value={filters.merchant}
          onChange={(merchant) => setFilter({ merchant })}
          options={[
            { value: '', label: 'Any partner' },
            ...(merchants.data?.rewardMerchants ?? []).map((m) => ({ value: m.name, label: m.displayName ?? m.name })),
          ]}
        />
        {view === 'all' && (
          <Select
            variant="filter"
            aria-label="Status"
            size="sm"
            width={200}
            value={filters.state}
            onChange={(state) => setFilter({ state, owedBefore: '' })}
            options={STATE_OPTIONS}
          />
        )}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: '40px' }}>
            Settled
          </Typography>
          <Box sx={{ width: 170 }}>
            <Input aria-label="Settled from" type="date" size="sm" value={filters.from} onChange={(from) => setFilter({ from })} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: '40px' }}>
            to
          </Typography>
          <Box sx={{ width: 170 }}>
            <Input aria-label="Settled to" type="date" size="sm" value={filters.to} onChange={(to) => setFilter({ to })} />
          </Box>
        </Stack>
        {filtered && (
          <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
            <Button variant="link" size="sm" onClick={() => setFilter(FILTER_DEFAULTS)}>
              Clear
            </Button>
          </Box>
        )}
      </Stack>

      <Card variant="elevated" padding="none">
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <DataTableBody
            columns={COLUMNS.length}
            isLoading={payments.isLoading}
            isError={payments.isError}
            isEmpty={rows.length === 0}
            loadingRows={8}
            emptyMessage={
              view === 'review'
                ? 'Nothing to review.'
                : view === 'owed'
                  ? 'Nothing owed too long.'
                  : filtered
                    ? 'No payments match.'
                    : 'No payments yet. They appear when a customer pays a partner by Pix.'
            }
            errorMessage="Could not load payments."
          >
            {rows.map((p) => {
              const state = paymentState(p.state);
              const step =
                p.state === RewardPayment_State.STATE_PAYING && p.redemption ? redemptionState(p.redemption.state) : null;
              return (
                <TableRow key={p.name} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(p)}>
                  <TableCell>{p.settleTime ? formatOrderDate(p.settleTime) : '—'}</TableCell>
                  <TableCell>{p.userId ? <UserIdLink userId={p.userId} /> : '—'}</TableCell>
                  <TableCell>{p.merchant ? (merchantNames.get(p.merchant) ?? p.merchantDisplayName ?? p.merchant) : '—'}</TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{usdText(p.paidUsd)}</TableCell>
                  <TableCell>{choiceText(p)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StatusPill tone={state.tone} label={state.label} size="sm" />
                      {step && (
                        <Typography variant="caption" color="text.secondary">
                          {step.label}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </DataTableBody>
        </Table>
      </Card>

      <CursorPagination
        firstRowNumber={pagination.firstRowNumber}
        count={rows.length}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={!!nextPageToken}
        onPreviousPage={pagination.goToPreviousPage}
        onNextPage={() => pagination.goToNextPage(nextPageToken)}
        disabled={payments.isLoading}
      />

      <PaymentDetails
        payment={selected}
        merchantName={
          selected?.merchant ? (merchantNames.get(selected.merchant) ?? selected.merchantDisplayName ?? 'Partner') : 'Partner'
        }
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}
